import {
  faCheck,
  faClock,
  faLocationDot,
  faSave,
  faTriangleExclamation,
  faUserGroup,
  faUserPen,
  faVideo,
  faXmark
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { type ReactElement, useContext, useState } from 'react'
import { z } from 'zod'
import {
  Dropdown,
  Form,
  InputCol,
  Submit,
  TextareaInput,
  TextInput
} from '~/components/Form'
import { DateInput } from '~/components/Form/Input/DateInput'
import { SubmitAlignment } from '~/components/Form/SubmitAlignment'
import { LabelWithIcon } from '~/components/Global/Elements/LabelWithIcon/LabelWithIcon'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import {
  ButtonTabGroup,
  type ButtonTabOption
} from '~/components/Global/Interactions/ButtonTabGroup/ButtonTabGroup'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import { AsideAction } from '~/components/Scopes/Actions/AsideAction'
import { AsideActionsItems } from '~/components/Scopes/Actions/AsideActionsItems'
import { useCloseAction } from '~/components/Scopes/Actions/hooks/useCloseAction'
import { DetailPanel } from '~/domains/actions/components/DetailPanel'
import { DetailToggle } from '~/domains/actions/components/DetailToggle'
import type { Action } from '~/domains/actions/types'
import { toast } from '~/utils'
import { AsideActionContext } from '~/utils/contextes'
import { formatDateTimeShort } from '~/utils/date'
import { formatName } from '~/utils/names'
import { transformUrlIntoLink } from '~/utils/urls'
import { optionalDate, requiredDate } from '~/utils/zod'
import { useAssigneeOptions } from '../../hooks/useAssigneeOptions'
import { useUpdateTaskMutation } from '../../mutations/updateTask'
import { formatTaskSchedule } from '../../utils/formatTaskSchedule'
import { getTaskDueDate, isTaskOverdue } from '../../utils/groupTasksByDue'
import {
  getTaskIcon,
  getTaskTitle,
  getTaskWordingFor,
  hasTaskOutcome,
  isFreeTask as isFreeTaskType
} from '../../utils/taskType'
import { TaskAside } from './TaskAside'
import {
  participantListInAside,
  taskAsideSection,
  taskAsideStatusRow,
  taskRecap,
  taskRecapOverdue
} from './TaskAside.css'
import { TaskParticipants } from './TaskParticipants'

type TaskStatus = 'completed' | 'not_completed' | 'cancelled'

const outcomeOptions: ButtonTabOption<TaskStatus>[] = [
  { label: 'Abouti', value: 'completed', icon: faCheck, variant: 'success' },
  {
    label: 'Non abouti',
    value: 'not_completed',
    icon: faTriangleExclamation,
    variant: 'warning'
  },
  { label: 'Annulé', value: 'cancelled', icon: faXmark, variant: 'error' }
]

const doneOptions: ButtonTabOption<TaskStatus>[] = [
  { label: 'Faite', value: 'completed', icon: faCheck, variant: 'success' },
  { label: 'Annulée', value: 'cancelled', icon: faXmark, variant: 'error' }
]

const createFormSchema = (completionRequired: boolean) =>
  z.object({
    notes: z.string().optional(),
    subject: z.string().optional(),
    scheduledDate: z.coerce.date().optional().nullable(),
    completionDate: completionRequired ? requiredDate() : optionalDate(),
    assignedUser: z.string().min(1, 'Veuillez choisir un utilisateur')
  })

type FormValues = z.infer<ReturnType<typeof createFormSchema>>

const getInitialStatus = (task: Action): TaskStatus | null => {
  if (task.isCancelled) {
    return 'cancelled'
  }
  if (task.isCompleted === true) {
    return 'completed'
  }
  if (task.isCompleted === false) {
    return 'not_completed'
  }
  return null
}

export const TaskAsideContent = ({ task }: { task: Action }): ReactElement => {
  const { setIsDirty } = useContext(AsideActionContext)
  const closeAction = useCloseAction()
  const { mutateAsync: updateTask } = useUpdateTaskMutation()
  const assigneeOptions = useAssigneeOptions(task.user)

  const returnTo = <TaskAside taskId={task.id} />
  const applicantIds = Object.values(task.applicants ?? {}).map(
    (applicant) => applicant.id
  )

  const initialStatus = getInitialStatus(task)
  const [status, setStatus] = useState<TaskStatus | null>(initialStatus)
  const [showEdit, setShowEdit] = useState(false)
  const completionRequired = status !== null && status !== initialStatus
  const isFreeTask = isFreeTaskType(task)
  const withOutcome = hasTaskOutcome(task)
  const wording = getTaskWordingFor(task)
  const dueDate = getTaskDueDate(task)
  const overdue = isTaskOverdue(task) && initialStatus === null

  const onSubmit = async (values: FormValues) => {
    const statusChanged = status !== initialStatus
    const nextAssignee = Number.parseInt(values.assignedUser, 10)
    const subjectChanged =
      isFreeTask && (values.subject ?? '') !== (task.subject ?? '')
    const scheduledChanged =
      values.scheduledDate !== undefined &&
      (values.scheduledDate?.getTime() ?? null) !== (dueDate?.getTime() ?? null)

    return updateTask(
      {
        taskId: task.id,
        applicantIds,
        notes: values.notes?.trim() ?? '',
        subject: subjectChanged ? values.subject : undefined,
        scheduledDate: scheduledChanged ? values.scheduledDate : undefined,
        assignedUserId:
          nextAssignee && nextAssignee !== task.user?.id
            ? nextAssignee
            : undefined,
        isCompleted:
          statusChanged && status !== null && status !== 'cancelled'
            ? status === 'completed'
            : undefined,
        isCancelled: statusChanged && status === 'cancelled' ? true : undefined,
        completionDate:
          statusChanged && status !== null
            ? (values.completionDate ?? new Date())
            : undefined
      },
      {
        onSuccess: () => {
          closeAction(true)
          toast(
            'Tâche mise à jour',
            'La tâche a bien été enregistrée',
            ToastType.Success
          )
          if (typeof window.paginatorHistoriqueActions !== 'undefined') {
            window.paginatorHistoriqueActions.refresh()
          }
        },
        onError: () =>
          toast(
            'Une erreur est survenue',
            "La tâche n'a pas pu être mise à jour",
            ToastType.Danger
          )
      }
    )
  }

  const externalParticipants = (task.externalParticipants ?? []).map(
    (participant) => participant.name ?? participant.email
  )

  return (
    <Form
      key={task.id}
      onSubmit={onSubmit}
      schema={createFormSchema(completionRequired)}
      defaultValues={{
        notes: task.notes ?? '',
        subject: task.subject ?? '',
        assignedUser: task.user ? String(task.user.id) : undefined
      }}
    >
      <AsideAction title={getTaskTitle(task)} icon={getTaskIcon(task)}>
        <AsideActionsItems>
          <Button
            variation={ButtonVariation.DangerInversed}
            type="button"
            onClick={(event) => {
              event.preventDefault()
              closeAction()
            }}
            buttonSize="small"
          >
            <FontAwesomeIcon icon={faXmark} />
          </Button>
          <Submit
            buttonVariation={ButtonVariation.Primary}
            submitAlignment={SubmitAlignment.Right}
            loadingText="Enregistrement..."
            successText="Enregistré !"
            onSuccessReset={false}
            buttonSize="small"
          >
            <FontAwesomeIcon icon={faSave} />
            Enregistrer
          </Submit>
        </AsideActionsItems>

        <TaskParticipants
          task={task}
          returnTo={returnTo}
          className={participantListInAside}
        />

        <div className={taskAsideSection}>
          <div className={taskRecap}>
            {task.createdBy && task.createdBy.id !== task.user?.id && (
              <LabelWithIcon icon={faUserPen}>
                {wording.created} par {formatName(task.createdBy)}
              </LabelWithIcon>
            )}
            <LabelWithIcon icon={faClock}>
              <span className={clsx(overdue && taskRecapOverdue)}>
                {dueDate
                  ? formatTaskSchedule(
                      dueDate,
                      task.scheduledDateEnd
                        ? new Date(task.scheduledDateEnd)
                        : null,
                      wording.scheduled
                    )
                  : 'Sans échéance'}
                {overdue ? ' (en retard)' : ''}
              </span>
            </LabelWithIcon>
            {task.completionDate && initialStatus !== null && (
              <LabelWithIcon icon={faCheck}>
                {initialStatus === 'cancelled'
                  ? wording.cancelled
                  : wording.treated}{' '}
                le {formatDateTimeShort(new Date(task.completionDate))}
              </LabelWithIcon>
            )}
            {task.resolvedAddress && (
              <LabelWithIcon icon={faLocationDot}>
                {transformUrlIntoLink(task.resolvedAddress)}
              </LabelWithIcon>
            )}
            {task.meetingLink && (
              <LabelWithIcon icon={faVideo}>
                <a href={task.meetingLink} target="_blank" rel="noreferrer">
                  Rejoindre la visioconférence
                </a>
              </LabelWithIcon>
            )}
            {externalParticipants.length > 0 && (
              <LabelWithIcon icon={faUserGroup}>
                {externalParticipants.join(', ')}
              </LabelWithIcon>
            )}
          </div>
        </div>

        <InputCol>
          <div className={taskAsideStatusRow}>
            <ButtonTabGroup
              options={withOutcome ? outcomeOptions : doneOptions}
              value={status}
              onChange={(value) => {
                setStatus(value)
                setIsDirty(true)
              }}
            />
            {completionRequired && (
              <DateInput
                label={`${wording.treated} le`}
                name="completionDate"
                required
                showTime
                defaultDate={new Date()}
                onChange={() => setIsDirty(true)}
              />
            )}
          </div>
          <TextareaInput
            label="Notes"
            name="notes"
            rows={4}
            placeholder={
              withOutcome
                ? "Comment s'est passé cet échange ?"
                : 'Ajouter des notes sur la tâche...'
            }
            onChange={() => setIsDirty(true)}
          />
          <DetailToggle
            isOpen={showEdit}
            onToggle={() => setShowEdit((open) => !open)}
          />
          <DetailPanel isOpen={showEdit}>
            <InputCol>
              {isFreeTask && (
                <TextInput
                  label="Sujet"
                  name="subject"
                  onChange={() => setIsDirty(true)}
                />
              )}
              <DateInput
                label={`${wording.scheduled} le`}
                name="scheduledDate"
                showTime
                noPast
                defaultDate={dueDate}
                onChange={() => setIsDirty(true)}
              />
              {assigneeOptions.length > 0 && (
                <Dropdown
                  label={`${wording.assigned} à`}
                  name="assignedUser"
                  required
                  options={assigneeOptions}
                  defaultValue={task.user ? String(task.user.id) : undefined}
                  onChange={() => setIsDirty(true)}
                />
              )}
            </InputCol>
          </DetailPanel>
        </InputCol>
      </AsideAction>
    </Form>
  )
}
